import { SearchFilters } from "@/constants/filters";

// The room attributes we can compare against filters on the client. The detail
// payload carries these per sibling room (plus the current one via roomData).
export type RoomAttributes = {
  rentPrice?: string | null;
  gender?: string | null;
  petFriendly?: boolean | null;
  contract?: boolean | null;
  smokingAllowed?: boolean | null;
  availableFrom?: string | null;
};

// Active room filters, already parsed from the URL query record.
export type RoomFilterValues = {
  minRentPrice?: number;
  maxRentPrice?: number;
  gender?: string;
  petFriendly?: boolean;
  contract?: boolean;
  smokingAllowed?: boolean;
  availableFrom?: string;
};

export type RoomMismatch =
  | { type: "priceAbove"; price: number; max: number }
  | { type: "priceBelow"; price: number; min: number }
  | { type: "gender" }
  | { type: "petFriendly" }
  | { type: "contract" }
  | { type: "smokingAllowed" }
  | { type: "availability"; availableFrom: string | null; filter: string }
  | { type: "generic" };

export function parseRoomFilters(
  params?: Record<string, string | null | undefined>,
): RoomFilterValues {
  if (!params) return {};
  const num = (key: SearchFilters) => {
    const raw = params[key];
    if (!raw) return undefined;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? undefined : parsed;
  };
  const str = (key: SearchFilters) => params[key] || undefined;
  const bool = (key: SearchFilters) =>
    params[key] === "true" ? true : undefined;
  return {
    minRentPrice: num(SearchFilters.MIN_RENT_PRICE),
    maxRentPrice: num(SearchFilters.MAX_RENT_PRICE),
    gender: str(SearchFilters.GENDER),
    petFriendly: bool(SearchFilters.PET_FRIENDLY),
    contract: bool(SearchFilters.CONTRACT),
    smokingAllowed: bool(SearchFilters.SMOKING_ALLOWED),
    availableFrom: str(SearchFilters.AVAILABLE_FROM),
  };
}

// Best-effort explanation of why a room is outside the active filters, checked
// in order of the clearest signal first. The in/out decision itself stays with
// the backend (it owns the availability-window logic); this only labels the
// reason from the data the client has. Falls back to a generic reason when the
// cause is something we can't read (e.g. features) or can't pin down.
export function getRoomMismatch(
  room: RoomAttributes,
  filters: RoomFilterValues,
): RoomMismatch {
  const price = room.rentPrice != null ? Number(room.rentPrice) : Number.NaN;
  if (!Number.isNaN(price)) {
    if (filters.maxRentPrice != null && price > filters.maxRentPrice) {
      return { type: "priceAbove", price, max: filters.maxRentPrice };
    }
    if (filters.minRentPrice != null && price < filters.minRentPrice) {
      return { type: "priceBelow", price, min: filters.minRentPrice };
    }
  }

  if (
    filters.gender &&
    filters.gender !== "any" &&
    room.gender &&
    room.gender !== filters.gender
  ) {
    return { type: "gender" };
  }

  if (filters.petFriendly === true && room.petFriendly === false) {
    return { type: "petFriendly" };
  }
  if (filters.contract === true && room.contract === false) {
    return { type: "contract" };
  }
  if (filters.smokingAllowed === true && room.smokingAllowed === false) {
    return { type: "smokingAllowed" };
  }

  if (filters.availableFrom) {
    return {
      type: "availability",
      availableFrom: room.availableFrom ?? null,
      filter: filters.availableFrom,
    };
  }

  return { type: "generic" };
}
